import React, { useEffect, useMemo, useState, startTransition } from "react";
import {
  convertSnakeToCamelCase,
  rgba2hex,
  useActiveCanvas,
  useActiveMainCanvas,
  useCanvasId,
} from "@/utils/utils";
import {
  Tabs as TabsUnstyled,
  TabsList as TabsListUnstyled,
  Tab as TabUnstyled,
  TabPanel as TabPanelUnstyled,
} from "@mui/base";
import {
  CSS_VALUES_TO_COLLECT,
  EXPANDABLE_CSS_VALUES,
  SCREEN_SIZE_NAME_TO_SIZE,
} from "@/utils/constants";
import { useDispatch, useSelector } from "react-redux";
import { convertExpandedDefinedStyles } from "@/utils/cssValueUtils";
import { TempoElement } from "@/utils/tempoElement";
import {
  useCanvasStore,
  useSelectedNavTreeNode,
} from "@/components/Canvas/stores/canvasStore";
import { X } from "lucide-react";
import useLayoutStore from "@/store/zustand/layoutStore";

import useDebouncedState from "@/hooks/useDebouncedState";
import { TailwindStylingPanel } from "./TailwindStylingPanelV2";
import { JsxElement } from "@/utils/jsx/jsxElementTypes";
import { useTypedSelector } from "@/store/store";
import { useIframeStore } from "@/components/Canvas/stores/iframeStore";
import { StoryControlsManager } from "./StoryControlsManager";
import DesignTokensPanel from "../DesignTokens/DesignTokensPanel";
import { useCodebaseStore } from "@/store/zustand/codebaseStore";
import { useDesignCommandManager } from "@/store/zustand/designCommandManagerStore";
import {
  ChangeType,
  RedoChange,
  UndoChange,
} from "@/store/zustand/changeLedgerTypes";

// Matches tempo-devtools, used for instant updates
const MESSAGE_AGGREGATE_TIME_MS = 100;

interface Props {}

// The value of a css field
export interface CssFieldValue {
  value?: string;
  cssName: string;
  evaluatedValue?: string;
}

/**
 * CSS Rule returned from the iframes
 */
export interface CssRule {
  filename?: string;
  selector?: string;
  atrule?: string;
  codebaseId?: string;
  source?: string;
  styles?: { [cssKey: string]: string };
  inherited?: boolean;
  applied?: boolean; // Whether this css rule is applied to the current element
  removable?: boolean; // Whether this css rule is removable with a simple class change
}

const RightStyle: React.FC<Props> = (props) => {
  let { selectedNavTreeNode, storyboardIdSelected } = useSelectedNavTreeNode();

  // If dragging the temp storyboard, show the stylign panel for the original element
  const tempDraggingStoryboard = useCanvasStore(
    (state) => state.temporaryStoryboard,
  );
  const elementKeyToNavNodeLookup = useCanvasStore(
    (state) => state.elementKeyToNavNodeLookup,
  );
  if (
    tempDraggingStoryboard &&
    tempDraggingStoryboard?._elementDragData?.selectedElementKey
  ) {
    selectedNavTreeNode =
      elementKeyToNavNodeLookup[
        tempDraggingStoryboard?._elementDragData?.selectedElementKey
      ];
    storyboardIdSelected = null;
  }

  const selectedElementCssEvals = useCanvasStore(
    (state) => state.selectedElementCssEvals,
  );

  const collapseRight = useLayoutStore((state) => state.collapseRight);

  const canvas = useActiveCanvas();

  const treeElementLookup = useCodebaseStore(
    (state) => state.treeElementLookup,
  );

  // For the left nav
  const selectedTreeElement =
    treeElementLookup?.[selectedNavTreeNode?.tempoElement?.codebaseId || ""];

  const [iframeValuesDebounced, setIFrameValuesDebounced] = useDebouncedState(
    {
      selectedElementCssEvals,
    },
    MESSAGE_AGGREGATE_TIME_MS,
  );

  useEffect(() => {
    setIFrameValuesDebounced({ selectedElementCssEvals });
  }, [selectedElementCssEvals, setIFrameValuesDebounced]);

  const mainCanvas = useActiveMainCanvas();
  const isSubcanvas = canvas && mainCanvas.id !== canvas.id;
  const showStoryControls = isSubcanvas && storyboardIdSelected;
  const showStyleControls = !showStoryControls;

  const defaultTab = showStoryControls ? "story" : "styles";
  const [activeTab, setActiveTab] = useState(defaultTab);

  useEffect(() => {
    // If only one is shown, set the active tab to the one that is shown
    if (showStoryControls && !showStyleControls) {
      setActiveTab("story");
    } else if (!showStoryControls && showStyleControls) {
      setActiveTab("styles");
    }
  }, [showStoryControls, showStyleControls]);

  /**
   * This use memo processes all the info taken from the iframe and converts
   * it into a format required by the styling panel
   */
  const {
    cssNameToFieldValue,
    parentCssEvals,
  }: {
    cssNameToFieldValue: { [cssName: string]: CssFieldValue };
    parentCssEvals: { [cssName: string]: string };
  } = useMemo(() => {
    const { selectedElementCssEvals } = iframeValuesDebounced;

    // The values of the css fields as evaluated from the DOM
    const cssNameToFieldValue: { [cssName: string]: CssFieldValue } = {};

    CSS_VALUES_TO_COLLECT.forEach((cssName: string) => {
      cssNameToFieldValue[cssName] = {
        cssName,
      };

      // For styles that can be set in different ways (e.g. padding-horizontal, padding, padding-left/right, etc)
      // Convert them out to their individual parts (top, right, bottom, left)
      let cssEvalsStyling = (selectedElementCssEvals?.[cssName] || null) as
        | string
        | null;

      // If it is expandable, the try to get the value from the individual parts if those are defined
      // E.g. we can get padding-right from the padding value
      if (EXPANDABLE_CSS_VALUES.has(cssName)) {
        // We also expand out the css evals
        cssEvalsStyling = convertExpandedDefinedStyles(
          cssName,
          undefined,
          selectedElementCssEvals,
        );
      }

      cssNameToFieldValue[cssName].evaluatedValue =
        cssEvalsStyling || undefined;

      // Always use the evaluated color because it will always be an rgba.
      if (cssName.includes("color")) {
        cssNameToFieldValue[cssName].evaluatedValue = rgba2hex(cssEvalsStyling);
      }
    });

    const parentCssEvals: { [cssName: string]: string } =
      selectedElementCssEvals?.["parent"] as unknown as {
        [cssName: string]: string;
      };

    return { cssNameToFieldValue, parentCssEvals };
  }, [iframeValuesDebounced]);

  const lastChangeApplied = useDesignCommandManager(
    (state) => state.serverChangeQueue[state.serverChangeQueue.length - 1],
  );

  const elementClasses = useMemo(() => {
    if (!lastChangeApplied?.needToProcessChange()) {
      return selectedTreeElement?.removableClasses || [];
    }

    // For instant updates so the value is most up-to-date
    let classesToReturn = selectedTreeElement?.removableClasses || [];

    let changeToCheck = lastChangeApplied,
      isUndo = false;
    if (changeToCheck?.type === ChangeType.REDO) {
      changeToCheck = (changeToCheck as RedoChange).changeFields.changeToRedo;
    } else if (changeToCheck?.type === ChangeType.UNDO) {
      changeToCheck = (changeToCheck as UndoChange).changeFields.changeToUndo;
      isUndo = true;
    }

    if (changeToCheck?.type === ChangeType.UPDATE_CLASSES) {
      if (isUndo) {
        classesToReturn = changeToCheck.changeFields.oldClasses;
      } else {
        classesToReturn = changeToCheck.changeFields.newClasses;
      }
    } else if (
      changeToCheck?.type === ChangeType.UPDATE_STORYBOARD &&
      changeToCheck.changeFields.additionalGroupedChange?.type ===
        ChangeType.UPDATE_CLASSES
    ) {
      if (isUndo) {
        classesToReturn =
          changeToCheck.changeFields.additionalGroupedChange.changeFields
            .oldClasses;
      } else {
        classesToReturn =
          changeToCheck.changeFields.additionalGroupedChange.changeFields
            .newClasses;
      }
    }

    return classesToReturn;
  }, [selectedTreeElement, lastChangeApplied]);

  // State for deferred values to improve performance
  // State for deferred values to improve performance
  const [deferredCssValues, setDeferredCssValues] =
    useState(cssNameToFieldValue);
  const [deferredParentCssEvals, setDeferredParentCssEvals] =
    useState(parentCssEvals);
  const [deferredElementClasses, setDeferredElementClasses] =
    useState(elementClasses);

  // Update deferred values using startTransition to keep UI responsive
  useEffect(() => {
    startTransition(() => {
      setDeferredElementClasses(elementClasses);
      setDeferredCssValues(cssNameToFieldValue);
      setDeferredParentCssEvals(parentCssEvals);
    });
  }, [elementClasses, cssNameToFieldValue, parentCssEvals]);

  // Show Design Tokens without tabs when no element is selected
  const shouldShowDesignTokens = !showStoryControls && !selectedNavTreeNode;

  return (
    <div
      className="right-style hide-scrollbar"
      data-mouse-down-id="styling-panel"
    >
      {/* Always render DesignTokensPanel but hide it when not needed */}
      <div
        className={`w-full h-full ${
          shouldShowDesignTokens ? "block" : "hidden"
        }`}
      >
        <DesignTokensPanel />
      </div>

      {/* Render tabs when not showing design tokens */}
      {!shouldShowDesignTokens && (
        <TabsUnstyled
          defaultValue={defaultTab}
          value={activeTab}
          onChange={(e: any, val: string) => val && setActiveTab(val)}
        >
          <TabsListUnstyled className="tab-list w-full">
            {showStoryControls && (
              <TabUnstyled className="tab" value={"story"}>
                Story Controls
              </TabUnstyled>
            )}
            {showStyleControls && (
              <TabUnstyled className="tab" value={"styles"}>
                Styles
              </TabUnstyled>
            )}
            <div className="grow flex flex-row justify-end pr-5">
              <X
                className="w-4 h-4 cursor-pointer text-secondary-800 hover:text-secondary-900"
                onClick={() => collapseRight()}
              />
            </div>
          </TabsListUnstyled>
          <TabPanelUnstyled value={"story"}>
            <StoryControlsManager />
          </TabPanelUnstyled>
          <TabPanelUnstyled value={"styles"}>
            <TailwindStylingPanel
              cssValues={deferredCssValues}
              parentCssEvals={deferredParentCssEvals}
              selectedCodebaseId={
                selectedNavTreeNode?.tempoElement?.codebaseId || ""
              }
              treeElementType={selectedTreeElement?.type}
              classList={deferredElementClasses}
              selectedTopStoryboardId={storyboardIdSelected ?? undefined}
            />
          </TabPanelUnstyled>
        </TabsUnstyled>
      )}
    </div>
  );
};

export default React.memo(RightStyle);
